import React, { type FunctionComponent } from 'react'
import { css } from '@emotion/react'
import { Button, TextArea } from '@retailer-platform/shared-components'
import { spacing } from 'gin-and-tonic/foundation/spacing'
import { ErrorMessage } from 'gin-and-tonic/common/error-message/ErrorMessage'
import { useCodeLookupMatcher } from 'domains/collections/components/collections-form/components/code-lookup-matcher/utils/CodeLookupMatcher.hooks'
import { type CodeLookupsCodeType } from 'domains/collections/components/collections-form/components/code-lookup-matcher/utils/CodeLookupMatcher.types'
import { useWarehouseContext } from 'utils/contexts/warehouse/WarehouseContext.hooks'

interface Props {
  // callback when codeLookupBatch is created
  onCodeLookupBatchCreation: (codeLookupBatchId: string) => void
  // callback when cancel button is clicked
  onCancel: () => void
  // UPC or RRC matching
  codeType: CodeLookupsCodeType
  // Required for RRC matching
  retailerId?: string
}

const pageWrapper = css({ height: 400, display: 'flex', flexDirection: 'column' })

const contentWrapper = css({
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
})

const buttonsWrapper = css({
  display: 'flex',
  justifyContent: 'flex-end',
})

const errorsWrapper = css({
  height: spacing.X44,
})

const textAreaStyles = css({
  flex: 1,
  marginBottom: spacing.X24,
  marginLeft: spacing.X8,
  marginRight: spacing.X8,
  padding: spacing.X12,
})

export const codeLookupMatcherTestIDs = {
  textArea: 'add-by-upc-textarea',
  cancelButton: 'add-by-upc-cancel-button',
  submitButton: 'add-by-upc-submit-button',
}

export const CodeLookupMatcher: FunctionComponent<Props> = ({
  onCancel,
  onCodeLookupBatchCreation,
  codeType,
  retailerId,
}) => {
  // use default localeCode for now
  const localeCode = useWarehouseContext().warehouse.defaultLocale.localeCode.toUpperCase()
  const {
    error,
    handleSubmit,
    handleTextAreaChange,
    messages,
    validationErrorMessages,
    submitDisabled,
  } = useCodeLookupMatcher({ onCodeLookupBatchCreation, codeType, retailerId, localeCode })

  const { textAreaPlaceholderMessage, cancelButtonMessage, submitButtonMessage } = messages

  const validationErrorsBody = validationErrorMessages.map(errorMessage => (
    <ErrorMessage key={errorMessage}>{errorMessage}</ErrorMessage>
  ))

  return (
    <div css={pageWrapper}>
      <div css={contentWrapper}>
        <TextArea
          data-testid={codeLookupMatcherTestIDs.textArea}
          onChange={handleTextAreaChange}
          placeholder={textAreaPlaceholderMessage}
          css={textAreaStyles}
        />
        <div css={errorsWrapper}>
          {validationErrorsBody}
          {error && <ErrorMessage>{messages.submissionError}</ErrorMessage>}
        </div>
        <div css={buttonsWrapper}>
          <Button
            data-testid={codeLookupMatcherTestIDs.cancelButton}
            onClick={onCancel}
            variant="detrimental"
          >
            {cancelButtonMessage}
          </Button>
          <Button
            data-testid={codeLookupMatcherTestIDs.submitButton}
            disabled={submitDisabled}
            onClick={handleSubmit}
          >
            {submitButtonMessage}
          </Button>
        </div>
      </div>
    </div>
  )
}
